/**
 * Component for displaying a fur detection animation.
 *
 * @component
 * @param {Object} entryFields - The entry fields containing image URLs.
 * @returns {JSX.Element} - FurDetectionAnimation component.
 */

import React, { useEffect, useState } from "react";
import Image from "next/image";
import ZigZag from "../../../assets/svg/ZigZagIcon";
import { motion, useAnimation, useInView } from "framer-motion";
import { useRef } from "react";

const FurDetectionAnimation = ({ entryFields }) => {
  const ref = useRef();
  const inView = useInView(ref, { once: true });

  const furDetactMessage = useAnimation();
  const furCleaningMessage = useAnimation();
  const furImage = useAnimation();
  const hotzoneFurImage = useAnimation();

  useEffect(() => {
    let timerId = null;
    let hotzoneFurImgTimerId = null;

    if (inView) {
      furDetactMessage.start({
        visibility: "visible",
        transition: {
          delay: 0.5,
          duration: 1,
          type: "smooth",
        },
      });
      timerId = setTimeout(() => {
        hotzoneFurImage.start({
          opacity: 1,
          scale: 1,
          transition: {
            delay: 0,
            duration: 1,
            type: "spring",
          },
        });

        furCleaningMessage.start({
          opacity: 1,
          transition: {
            delay: 2,
            duration: 0.3,
            type: "smooth",
          },
        });

        furImage.start({
          opacity: 0,
          transition: {
            delay: 3,
            duration: 1,
            type: "smooth",
          },
        });

        furDetactMessage.start({
          visibility: "hidden",
          transition: {
            delay: 3,
            duration: 0.2,
            type: "smooth",
          },
        });

        hotzoneFurImgTimerId = setTimeout(() => {
          hotzoneFurImage.start({
            opacity: 0,
            scale: 0,
            transition: {
              delay: 1,
              duration: 1,
              type: "smooth",
            },
          });

          furCleaningMessage.start({
            opacity: 0,
            transition: {
              delay: 1,
              duration: 1,
              type: "smooth",
            },
          });
        }, 3000);
      }, 1500);
    }

    return () => {
      clearTimeout(timerId);
      clearTimeout(hotzoneFurImgTimerId);
    };
  }, [inView]);

  return (
    <div
      className={`w-[21.59%] absolute left-[14.5%] -bottom-[2.5%]`}
      ref={ref}
    >
      <div className="aspect-w-311 aspect-h-240">
        <div className="h-full w-full">
          <div className="h-full relative">
            <motion.div
              className={`absolute left-[35.5%] top-[7%] z-[1] flex justify-center items-center rounded-[10px] py-[7px] px-[16px] text-white bg-blue w-max`}
              initial={{ visibility: "hidden" }}
              animate={furDetactMessage}
            >
              &bull;
              <span className={`eyebrow-bold md:pl-[8px] pl-[6px]`}>
                fur Detected
              </span>
            </motion.div>
            <motion.div
              className={`absolute left-[35.5%] top-[7%] z-[1] flex justify-center items-center rounded-[10px] py-[7px] px-[16px] text-white bg-blue w-max`}
              initial={{ opacity: 0 }}
              animate={furCleaningMessage}
            >
              <ZigZag color={"#ffff"} />
              <span className={`eyebrow-bold md:pl-[8px] pl-[6px]`}>
                Vacuuming Fur
              </span>
            </motion.div>
            <motion.div
              className={`w-[95%] absolute`}
              initial={{ opacity: 1 }}
              animate={furImage}
            >
              <div className="aspect-w-15 aspect-h-13">
                <Image
                  src={`https:${entryFields?.furImage?.fields?.file?.url}`}
                  layout="fill"
                  alt="Fur Image"
                  className="custom-img"
                />
              </div>
            </motion.div>
            <motion.div
              className={`w-[77.17%] absolute -z-[1]`}
              initial={{ opacity: 0, scale: 0 }}
              animate={hotzoneFurImage}
            >
              <div className="aspect-w-1 aspect-h-1">
                <Image
                  src={`https:${entryFields?.furHotzoneImage?.fields?.file?.url}`}
                  layout="fill"
                  alt="hotzone - Fur animation"
                  className="custom-img"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FurDetectionAnimation;
